import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import React, { useMemo } from 'react';

type Props = {
  onNextPage: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onPreviousPage: () => void;
  page: number;
  pageSize?: number;
  totalPages: number;
  totalResults: number;
};

const MAX_ITEMS = 9;

export const TablePagination: React.FC<Props> = props => {
  const { onNextPage, onPageChange, onPageSizeChange, onPreviousPage, page, pageSize = 10, totalPages, totalResults } = props;
  const { t } = useTranslationWithPrefix('components.table.pagination');

  const handlePageChange = (page: number) => () => {
    onPageChange(page);
  };

  const firstResult = page * pageSize + 1;
  const lastResult = page === totalPages - 1 ? totalResults : firstResult + pageSize - 1;

  const getFirstPage = () => {
    if (totalPages <= MAX_ITEMS || page <= 4) {
      return 0;
    }

    if (page > totalPages - 5) {
      return totalPages - MAX_ITEMS;
    }

    return page - 4;
  };

  const getLastPage = () => {
    if (totalPages <= MAX_ITEMS) {
      return totalPages;
    }
    if (totalPages <= MAX_ITEMS || page < 5) {
      return 9;
    }

    if (page > totalPages - 5) {
      return totalPages;
    }

    return page + 5;
  };

  const items = useMemo(() => {
    if (totalPages === 0) {
      return [];
    }
    const first = getFirstPage();
    const last = getLastPage();
    const itemsInner = [];
    for (let i = first; i < last; i++) {
      itemsInner.push(
        <li
          aria-current={page === i}
          className={`page-item cursor-pointer ${page === i ? 'active' : ''}`}
          key={i}
        >
          <a className="page-link" onClick={handlePageChange(i)}>
            {i + 1}
          </a>
        </li>,
      );
    }
    return itemsInner;
  }, [page, totalPages]);

  return (
    <nav
      aria-label={t('navigation')}
      className="flex justify-content-between flex-row align-items-center"
    >
      <div className="d-flex align-items-center gap-3">
        <div>
          {`${firstResult} - ${lastResult} ${t('of')} ${totalResults}`}{' '}
        </div>
        {onPageSizeChange && (
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: '0.9rem' }}>{t('rowsPerPage')}</span>
            <select 
              className="form-select form-select-sm" 
              onChange={onPageSizeChange}
              style={{ width: 'auto' }}
              value={pageSize}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        )}
      </div>
      <div>
        <ul className="pagination my-0">
          <li
            className={`page-item ${page === 0 ? 'disabled' : ''} cursor-pointer`}
            onClick={onPreviousPage}
          >
            <a className="page-link">{t('previous')}</a>
          </li>
          {items}
          <li
            className={`page-item  cursor-pointer ${totalPages === 0 || page === totalPages - 1 ? 'disabled' : ''}`}
            onClick={onNextPage}
          >
            <a aria-label={t('next')} className="page-link">
              {t('next')}
            </a>
          </li>
        </ul>
      </div>
    </nav>
  );
};
